import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { FormsTemplateInputFilter } from "domain/models/nimberforms-input-filters";
import { Logger } from "infra/logger/logger";
import formsTemplateRepository, { formTemplateUpdateInput } from "infra/repositories/formstemplate-repository";

export const useFormTemplateByFilter = (filter: FormsTemplateInputFilter) =>{
    const logger = Logger.getInstance();
    return useQuery({
        queryKey: ["nimberForms", filter],
        queryFn: async () => {
            try{
                const result = await formsTemplateRepository.getAll(filter);
                return result;
            }catch(caught){
                if (caught instanceof AxiosError) {
                    logger.error(
                        caught.response?.data.metadata.errors[0],
                        caught.response?.data.metadata
                    );
                    throw new Error(caught.response?.data.metadata.errors);
                }
                throw caught;
            }
        },
        staleTime: 0,
        enabled: true,
    })
}

export const useFormTemplateById = (id: string) => {
    const logger = Logger.getInstance();

    return useQuery({
        queryKey: ["nimberForms", id],
        queryFn: async () => {
            try{
                if (!id) throw new Error("ID inválido");
                const result = await formsTemplateRepository.getById(id);
                return result
            }catch(caught){
                if (caught instanceof AxiosError) {
                    logger.error(
                        caught.response?.data.metadata.errors[0],
                        caught.response?.data.metadata
                    );
                    throw new Error(caught.response?.data.metadata.errors);
                }
                throw caught;
            }
        },
        retry: false,
        staleTime: 0,
        enabled: true,
    })
}

export const useCreateTemplateMutation = () => {
    const logger = Logger.getInstance();
    const queryFormTemplate = useQueryClient();

    return useMutation({
        mutationFn: async (template: {title: string, description: string, config: string, active: boolean}) => {
            const result = await formsTemplateRepository.createTemplate(template);
            return result;
        },
        onSuccess: () => {
            queryFormTemplate.invalidateQueries({queryKey: ["nimberForms", {}]});
        },
        onError: (error) => {
            if (error instanceof AxiosError) {
                console.log("raw error:", error)
                if (error.response?.data?.metadata?.errors) {
                    const errors = error.response.data.metadata.errors;
                    logger.error(errors[0], error.response?.data.metadata);
                    console.log("error axios",errors[0]);
                    //Cria um objeto de erro com o array de erros como propriedade
                    const customError = new Error("Erro ao criar template do form");
                    (customError as any).errors = errors; // Adiciona o array de erros ao objeto de erro
                    throw customError;
                } else {
                    throw new Error("Erro desconhecido da API");
                }
            } else {
                logger.error("Erro desconhecido", error);
                throw error;
            }
        }
    });
}

export const useUpdateTemplateMutation = () => {
    const logger = Logger.getInstance();
    const queryFormTemplate = useQueryClient();

    return useMutation({
        mutationFn: async (input: formTemplateUpdateInput) => {
            const result = await formsTemplateRepository.updateTemplate(input);
            return result;
        },
        onSuccess: () => {
            queryFormTemplate.invalidateQueries({queryKey: ["nimberForms", {}]});
        },
        onError: (error) => {
            if (error instanceof AxiosError) {
                console.log("raw error:", error)
                if (error.response?.data?.metadata?.errors) {
                    const errors = error.response.data.metadata.errors;
                    logger.error(errors[0], error.response?.data.metadata);
                    console.log("error axios",errors[0]);
                    //Cria um objeto de erro com o array de erros como propriedade
                    const customError = new Error("Erro ao fazer update do template do form");
                    (customError as any).errors = errors; // Adiciona o array de erros ao objeto de erro
                    throw customError;
                } else {
                    throw new Error("Erro desconhecido da API");
                }
            } else {
                logger.error("Erro desconhecido", error);
                throw error;
            }
        }
    });
}

export const useDeleteTemplateMutation = () => {
    const logger = Logger.getInstance();
    const queryNimberForms = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const result = await formsTemplateRepository.deleteTemplate(id);
            return result;
        },
        onSuccess: () => {
            queryNimberForms.invalidateQueries({queryKey: ["nimberForms", {}]});
        },
        onError: (error) => {
            if (error instanceof AxiosError) {
                console.log("raw error:", error)
                if (error.response?.data?.metadata?.errors) {
                    const errors = error.response.data.metadata.errors;
                    logger.error(errors[0], error.response?.data.metadata);
                    console.log("error axios",errors[0]);
                    //Cria um objeto de erro com o array de erros como propriedade
                    const customError = new Error("Erro ao fazer update do template do form");
                    (customError as any).errors = errors; // Adiciona o array de erros ao objeto de erro
                    throw customError;
                } else {
                    throw new Error("Erro desconhecido da API");
                }
            } else {
                logger.error("Erro desconhecido", error);
                throw error;
            }
        }
    });
}